import React from 'react';

import { DOCUMENT_TYPES } from '~constants/common';

import { useToast } from '~libs/toast';

import { AsyncActionIcon } from '~components';
import { faEnvelope, faFilePdf } from '~components/icon';
import { Row } from '~components/table-view-v3';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

interface ComponentProps {
  row?: Row;
}

const StatementActions: React.FC<ComponentProps> = ({ row }) => {
  const { generateSeparate, emailSeparate, isGenerating } = useDocumentBatchGeneration();
  const { showToast } = useToast();

  const hasClientEmail = typeof row?.client?.email === 'string' && row.client.email.trim().length > 0;

  const handlePdf = async () => {
    if (!row?.id) return;
    try {
      await generateSeparate(DOCUMENT_TYPES.SAH_STATEMENT.SINGLE, [row.id as string]).unwrap();
      showToast('success', 'Statement generation started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start statement generation';
      showToast('danger', message);
      console.error('Statement PDF generation failed:', error);
      throw error;
    }
  };

  const handleEmail = async () => {
    if (!row?.id || !hasClientEmail) return;
    try {
      await emailSeparate(DOCUMENT_TYPES.SAH_STATEMENT.SINGLE, [row.id as string]).unwrap();
      showToast('success', 'Statement email scheduled');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to schedule statement email';
      showToast('danger', message);
      console.error('Statement email scheduling failed:', error);
      throw error;
    }
  };

  return (
    <div className="d-flex align-items-center">
      <AsyncActionIcon
        id={`generate-statement-pdf-${row?.id}`}
        icon={faFilePdf}
        toolTip="Generate PDF"
        ariaLabel="Generate PDF"
        onClick={handlePdf}
        disabled={isGenerating}
        successTitle="Statement generation started"
        spinnerDataCy="statement-generate-spinner"
      />
      <AsyncActionIcon
        id={`email-statement-${row?.id}`}
        icon={faEnvelope}
        toolTip={hasClientEmail ? 'Email Statement' : 'Client has no email address'}
        ariaLabel={hasClientEmail ? 'Email Statement' : 'Client has no email address'}
        onClick={handleEmail}
        disabled={isGenerating || !hasClientEmail}
        showTooltipWhenDisabled={!hasClientEmail}
        successTitle="Statement email scheduled"
        spinnerDataCy="statement-email-spinner"
      />
    </div>
  );
};

export default StatementActions;
